import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createCustomerRequest,
  getCustomers,
  getSectors,
  removeCustomer,
  updateCustomerRequest,
} from "../../../../services/api/landing/customers/customersApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Customer created successfully!",
    ar: "تم إنشاء العميل بنجاح!",
  },
  addError: {
    en: "Failed to create customer.",
    ar: "فشل في إنشاء العميل.",
  },
  update: {
    en: "Customer updated successfully!",
    ar: "تم تحديث العميل بنجاح!",
  },
  updateError: {
    en: "Failed to update customer.",
    ar: "فشل في تحديث العميل.",
  },
  delete: {
    en: "Customer deleted successfully!",
    ar: "تم حذف العميل بنجاح!",
  },
  deleteError: {
    en: "Failed to delete customer.",
    ar: "فشل في حذف العميل.",
  },
};

export const fetchCustomers = createAsyncThunk(
  "customers/fetchCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCustomers();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch customers");
    }
  },
);

export const fetchSectors = createAsyncThunk(
  "customers/fetchSectors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSectors();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch sectors");
    }
  },
);

export const createCustomer = createAsyncThunk(
  "customers/createCustomer",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createCustomerRequest(formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create customer");
    }
  },
);

export const updateCustomer = createAsyncThunk(
  "customers/updateCustomer",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateCustomerRequest(id, formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update customer");
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  "customers/deleteCustomer",
  async (customerId, { rejectWithValue }) => {
    try {
      await removeCustomer(customerId);
      return customerId;
    } catch (error) {
      return rejectWithValue("Failed to delete customer");
    }
  },
);

const customerSlice = createSlice({
  name: "customers",
  initialState: {
    customerData: [],
    sectors: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customerData = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSectors.fulfilled, (state, action) => {
        state.sectors = action.payload;
      })
      .addCase(fetchSectors.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customerData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const index = state.customerData.findIndex(
          (customer) => customer.id === action.payload.id,
        );
        if (index !== -1) {
          state.customerData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customerData = state.customerData.filter(
          (customer) => customer.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default customerSlice.reducer;
